import { useId, useMemo, useState, type FormEvent } from "react";
import { ChevronDown } from "lucide-react";
import { Button, Modal, Picker, SegmentedControl, type PickerOption, DialogCancel } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useCapability } from "../../app/bandContext.js";
import {
  useCreateTourDay,
  useUpdateTourDay,
  useDeleteTourDay,
  useTourDayLinkOptions,
  type TourDayDetail,
} from "../../app/hooks/useShows.js";
import { useTourDayPay, useUpdateTourDayPay } from "../../app/hooks/useMoney.js";
import { useBandTeam, bandRoleLabel } from "../../app/hooks/useBandTeam.js";
import { orgInitials } from "../../app/hooks/useOrg.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { PersonPickerOption } from "../shared/PersonPickerOption.js";
import { AvMark } from "../shared/AvMark.js";
import styles from "./OffDayModal.module.scss";

type MemberRefDto = components["schemas"]["MemberRefDto"];

type DayKind = "off" | "travel";
type LocMode = "city" | "route";

/** The two day-kinds this modal writes — colour comes from the `--ac-color-event-{off,travel}` family.*/
const KINDS: { kind: DayKind; label: string }[] = [
  { kind: "off", label: "Off day" },
  { kind: "travel", label: "Travel day" },
];

export interface OffDayModalProps {
  /** An existing off/travel day to EDIT, or null/undefined to CREATE a new one. */
  existing?: TourDayDetail | null;
  onClose: () => void;
  /** Fired after a successful save/delete (the mutations already invalidate the ops-calendar). */
  onSaved?: () => void;
  /** CREATE pre-linked to this show (the show editor's "Travel day" / "Day off" buttons). The link is fixed to
   *  the show, and the show is offered in the picker even before a date narrows the list. */
  seedShowId?: string;
  seedShowLabel?: string | null;
  /** CREATE with this kind pre-selected. */
  seedKind?: DayKind;
}

/** Wall-clock `HH:mm` → `HH:mm:ss` for the TimeOnly binder; "" → null. */
function toWireTime(v: string): string | null {
  const t = v.trim();
  if (!t) return null;
  return t.length === 5 ? `${t}:00` : t;
}
/** `HH:mm:ss(.fff)` → the `HH:mm` a native time input renders. */
function fromWireTime(v: string | null | undefined): string {
  return v ? v.slice(0, 5) : "";
}
function centsToStr(c: number | null | undefined): string { return c == null ? "" : (c / 100).toFixed(2); }
function strToCents(s: string): number { const t = s.trim(); return t === "" ? 0 : Math.round(Number(t) * 100); }
function usd(cents: number | null | undefined): string {
  return cents == null ? "—" : `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ---- link-picker date formatting (local-date, never UTC — a bare YYYY-MM-DD is a wall-clock day) ----------
function parseLocalDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
}
function shortDow(iso: string): string {
  const dt = parseLocalDate(iso);
  return dt ? dt.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase() : "";
}
function monthDay(iso: string): string {
  const dt = parseLocalDate(iso);
  return dt ? `${dt.getMonth() + 1}/${dt.getDate()}` : "";
}
function monthName(dt: Date): string {
  return dt.toLocaleDateString(undefined, { month: "short" }).toUpperCase();
}
/** A run's date-column label: "NOV 19", "NOV 19–23", or "NOV 19 – DEC 2". Empty when nothing is linked yet. */
function monthDayRange(a: string | null | undefined, b: string | null | undefined): string {
  const da = parseLocalDate(a);
  if (!da) return "";
  const db = parseLocalDate(b);
  if (!db || (da.getMonth() === db.getMonth() && da.getDate() === db.getDate())) return `${monthName(da)} ${da.getDate()}`;
  if (da.getMonth() === db.getMonth()) return `${monthName(da)} ${da.getDate()}–${db.getDate()}`;
  return `${monthName(da)} ${da.getDate()} – ${monthName(db)} ${db.getDate()}`;
}
function plural(n: number, one: string): string { return `${n} ${one}${n === 1 ? "" : "s"}`; }

/** A rich link-picker row: a mono date column (stacked) + a bold title over a muted sub-line. */
function LinkRow({ dateTop, dateBot, title, sub }: { dateTop: string; dateBot?: string; title: string; sub?: string }) {
  return (
    <span className={styles.linkRow}>
      <span className={styles.linkDate}>
        <span>{dateTop}</span>
        {dateBot && <span>{dateBot}</span>}
      </span>
      <span className={styles.linkBody}>
        <span className={styles.linkTitle}>{title}</span>
        {sub && <span className={styles.linkSub}>{sub}</span>}
      </span>
    </span>
  );
}

/**
 * The Off / travel day modal (ART-240). An off/travel day is a first-class ENTRY with a REQUIRED link to a
 * confirmed show (within 3 days) or a run — that link is how the day attributes to a show's booking-list total.
 * Writes the money-FREE `tour-days` surface (kind, location, members, notes, the link); per-member PAY + the
 * pulled per-diem rate ride the SEPARATE money-gated `/pay` sub-resource, shown only to a money seat.
 */
export function OffDayModal({ existing, onClose, onSaved, seedShowId, seedShowLabel, seedKind }: OffDayModalProps) {
  const isEdit = !!existing?.tourDayId;
  const formId = useId();

  const canFullMoney = useCapability("money:full");
  const canPerShowMoney = useCapability("money:per_show");
  const canMoney = canFullMoney || canPerShowMoney;

  const create = useCreateTourDay();
  const update = useUpdateTourDay();
  const del = useDeleteTourDay();
  const updatePay = useUpdateTourDayPay();
  const busy = create.isPending || update.isPending || del.isPending || updatePay.isPending;

  const [kind, setKind] = useState<DayKind>(
    existing?.kind === "travel" ? "travel" : existing?.kind === "off" ? "off" : seedKind ?? "off",
  );
  const [date, setDate] = useState(existing?.date ?? "");
  const [mode, setMode] = useState<LocMode>(existing?.fromCity || existing?.toCity ? "route" : "city");
  const [city, setCity] = useState(existing?.city ?? "");
  const [fromCity, setFromCity] = useState(existing?.fromCity ?? "");
  const [toCity, setToCity] = useState(existing?.toCity ?? "");
  const [departTime, setDepartTime] = useState(fromWireTime(existing?.departTime));
  const [busCall, setBusCall] = useState(existing?.busCall ?? "");
  const [notes, setNotes] = useState(existing?.notes ?? "");
  // ART-240 — a day is ALWAYS an explicit member set (no whole-band toggle); members are chosen with the same
  // crew picker the rest of the editor uses.
  const [memberIds, setMemberIds] = useState<string[]>(() =>
    (existing?.members ?? []).map((m) => m.personId).filter((id): id is string => !!id),
  );
  // The required attribution link, as a `show:{id}` / `run:{id}` token. A create from the show editor is
  // pre-linked to that show.
  const [link, setLink] = useState<string>(() =>
    existing?.showId ? `show:${existing.showId}`
      : existing?.runId ? `run:${existing.runId}`
        : seedShowId ? `show:${seedShowId}` : "",
  );
  // Per-member day pay (money seat), personId → dollars string. Seeded from the pay read in edit mode.
  const [pay, setPay] = useState<Record<string, string>>({});
  const [payTouched, setPayTouched] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);
  const [err, setErr] = useState("");

  // Link options are keyed by the day's date — confirmed shows within 3 days, plus the band's runs.
  const linkOptions = useTourDayLinkOptions(date || undefined);
  // Pay read (money seat, edit mode): the per-diem rate + stored per-member pay.
  const payRead = useTourDayPay(existing?.tourDayId ?? undefined, canMoney && isEdit);

  // Seed the pay map once the read lands (edit mode); the readout rate comes straight off it.
  const seededPay = useMemo(() => {
    const map: Record<string, string> = {};
    for (const l of payRead.data?.members ?? []) if (l.personId) map[l.personId] = centsToStr(l.amountCents);
    return map;
  }, [payRead.data]);
  // A member's pay: their edit if any, else the seeded value from the pay read, else blank.
  const payFor = (personId: string) => pay[personId] ?? seededPay[personId] ?? "";
  const perDiemCents = payRead.data?.perDiemCents ?? null;

  function setMemberPay(personId: string, amount: string) {
    setPayTouched(true);
    setPay((prev) => ({ ...seededPay, ...prev, [personId]: amount }));
  }

  function done() {
    onSaved?.();
    onClose();
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!date) { setErr("Pick a date."); return; }
    if (mode === "route") {
      const f = fromCity.trim(); const t = toCity.trim();
      if ((f && !t) || (!f && t)) { setErr("A route needs both a from-city and a to-city."); return; }
    }
    if (memberIds.length === 0) { setErr("Add at least one member."); return; }
    if (!link) { setErr("Link this day to a show or a run."); return; }
    setErr("");

    const [linkKind, linkId] = link.split(":");
    const members: MemberRefDto[] = memberIds.map((personId) => ({ personId }));
    const body = {
      date, kind,
      city: mode === "city" ? city.trim() || null : null,
      fromCity: mode === "route" ? fromCity.trim() || null : null,
      toCity: mode === "route" ? toCity.trim() || null : null,
      departTime: toWireTime(departTime),
      busCall: busCall.trim() || null,
      notes: notes.trim() || null,
      appliesToAll: false,
      members,
      showId: linkKind === "show" ? linkId : null,
      runId: linkKind === "run" ? linkId : null,
    };

    try {
      const saved = isEdit
        ? await update.mutateAsync({ tourDayId: existing!.tourDayId!, body })
        : await create.mutateAsync(body);

      // Money seat: persist per-member pay AFTER the schedule save (the members must exist first), when pay
      // was actually edited.
      if (canMoney && payTouched && saved.tourDayId) {
        await updatePay.mutateAsync({
          tourDayId: saved.tourDayId,
          body: { members: memberIds.map((personId) => ({ personId, amountCents: strToCents(payFor(personId)) })) },
        });
      }
      done();
    } catch {
      // The mutation hooks already toast the server error; keep the modal open.
    }
  }

  function remove() {
    if (!existing?.tourDayId) return;
    del.mutate(existing.tourDayId, { onSuccess: done });
  }

  const loadedShows = linkOptions.data?.shows ?? [];
  const runs = linkOptions.data?.runs ?? [];
  // A show-editor create is pre-linked to a show the picker may not list yet (no date narrowing) — offer it so
  // the fixed link renders selected.
  const shows =
    seedShowId && !loadedShows.some((s) => s.showId === seedShowId)
      ? [{ showId: seedShowId, date: existing?.date ?? date ?? "", venueName: seedShowLabel ?? "This show", city: null }, ...loadedShows]
      : loadedShows;

  // Rich two-line rows, grouped RUNS then SHOWS (the ART-240 §04 mockup). `label` is the plain single-line
  // summary the trigger + AT read; `render` is the listbox row.
  const linkPickerOptions: PickerOption[] = [
    ...runs.map((r) => {
      const name = r.name || "Run";
      const venues = (r.shows ?? []).map((sh) => sh.venueName).filter(Boolean).join(", ");
      const count = r.showCount ?? 0;
      return {
        value: `run:${r.runId}`,
        label: `${name} · ${plural(count, "show")}`,
        group: "Runs",
        render: (
          <LinkRow
            // A run with nothing linked yet has no date range — say so rather than leaving a blank column.
            dateTop={monthDayRange(r.firstDate, r.lastDate) || "No dates"}
            title={name}
            sub={venues ? `${plural(count, "show")} · ${venues}` : plural(count, "show")}
          />
        ),
      };
    }),
    ...shows.map((s) => {
      const title = s.venueName || s.city || "Show";
      const sub = s.venueName && s.city ? s.city : undefined;
      return {
        value: `show:${s.showId}`,
        label: `${monthDay(s.date ?? "")} · ${title}`,
        group: "Shows",
        render: <LinkRow dateTop={shortDow(s.date ?? "")} dateBot={monthDay(s.date ?? "")} title={title} sub={sub ?? undefined} />,
      };
    }),
  ];

  return (
    <Modal
      title={isEdit ? "Edit off / travel day" : "Off / travel day"}
      size="lg"
      onClose={onClose}
      bodyAs="form"
      onSubmit={submit}
      footer={
        <>
          {isEdit &&
            (pendingDelete ? (
              <>
                <span className={styles.membersHint}>Delete this day?</span>
                <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => setPendingDelete(false)}>
                  Keep
                </Button>
                <Button type="button" variant="accent" size="sm" loading={del.isPending} onClick={remove}>
                  Delete
                </Button>
              </>
            ) : (
              <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => setPendingDelete(true)}>
                Delete
              </Button>
            ))}
          <span className={styles.footerSpacer} />
          <DialogCancel disabled={busy} />
          <Button type="submit" loading={create.isPending || update.isPending || updatePay.isPending}>
            {isEdit ? "Save day" : "Add day"}
          </Button>
        </>
      }
    >
      <div className={styles.grid}>
        <div className={`${styles.field} ${styles.fieldFull}`}>
          <span className={styles.label}>Kind</span>
          <div className={styles.kinds} role="group" aria-label="Day kind">
            {KINDS.map((k) => (
              <button
                key={k.kind}
                type="button"
                className={styles.kindChip}
                data-active={kind === k.kind}
                aria-pressed={kind === k.kind}
                style={{ ["--kind-color" as string]: `var(--ac-color-event-${k.kind})` }}
                onClick={() => setKind(k.kind)}
              >
                <span className={styles.kindDot} aria-hidden="true" />
                {k.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${formId}-date`}>Date</label>
          <input id={`${formId}-date`} className={styles.input} type="date" value={date}
                 onChange={(e) => setDate(e.target.value)} />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${formId}-depart`}>
            Departure time <span className={styles.optional}>optional</span>
          </label>
          <input id={`${formId}-depart`} className={styles.input} type="time" value={departTime}
                 onChange={(e) => setDepartTime(e.target.value)} />
        </div>

        <div className={`${styles.field} ${styles.fieldFull}`}>
          <span className={styles.label}>Location</span>
          <SegmentedControl<LocMode>
            label="Location kind" size="sm" variant="mono" value={mode} onChange={setMode}
            options={[{ value: "city", label: "Single city" }, { value: "route", label: "Route" }]}
          />
          {mode === "city" ? (
            <input className={`${styles.input} ${styles.cityInput}`} placeholder="e.g. Denver, CO"
                   aria-label="City" value={city} onChange={(e) => setCity(e.target.value)} />
          ) : (
            <div className={styles.route}>
              <input className={styles.input} placeholder="Nashville, TN" aria-label="From city"
                     value={fromCity} onChange={(e) => setFromCity(e.target.value)} />
              <span className={styles.routeArrow} aria-hidden="true">→</span>
              <input className={styles.input} placeholder="Denver, CO" aria-label="To city"
                     value={toCity} onChange={(e) => setToCity(e.target.value)} />
            </div>
          )}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${formId}-buscall`}>
            Bus call <span className={styles.optional}>optional</span>
          </label>
          <input id={`${formId}-buscall`} className={styles.input} placeholder="Hotel lobby"
                 value={busCall} onChange={(e) => setBusCall(e.target.value)} />
        </div>

        {/* ART-240 — the REQUIRED link. Date-aware: confirmed shows within 3 days of the chosen date, plus the
            band's runs. A day with no date yet can't list shows, so the picker asks for a date first. Shared
            @cntrl/ui Picker (grouped) so it reads like every other select in the app. */}
        <div className={styles.field}>
          <span className={styles.label}>Link to show or run</span>
          <Picker
            label="Link to show or run"
            value={link}
            onChange={setLink}
            options={linkPickerOptions}
            disabled={!date && !link}
            className={styles.linkPicker}
            triggerClassName={styles.linkTrigger}
            renderTrigger={({ selected, open }) => (
              <>
                <span className={styles.linkTriggerValue} data-empty={selected ? undefined : ""}>
                  {selected ? selected.label : date ? "Choose a show or run…" : "Pick a date first"}
                </span>
                <ChevronDown size={16} className={styles.linkTriggerChevron} data-open={open || undefined} aria-hidden="true" />
              </>
            )}
          />
          {date && linkOptions.isSuccess && shows.length === 0 && runs.length === 0 && (
            <p className={styles.membersHint}>No confirmed shows within 3 days, and no runs yet.</p>
          )}
        </div>

        {/* Members — a search-to-add box over ONE list of member rows (avatar · name · role · pay · remove),
            the crew search and the per-member pay combined in one control. No whole-band toggle: an off/travel
            day is always an explicit set. */}
        <div className={`${styles.field} ${styles.fieldFull}`}>
          <MembersField
            memberIds={memberIds}
            onChange={setMemberIds}
            canMoney={canMoney}
            payFor={payFor}
            onSetPay={setMemberPay}
            perDiemCents={perDiemCents}
          />
        </div>

        <div className={`${styles.field} ${styles.fieldFull}`}>
          <label className={styles.label} htmlFor={`${formId}-notes`}>
            Notes <span className={styles.optional}>optional</span>
          </label>
          <textarea id={`${formId}-notes`} className={`${styles.input} ${styles.textarea}`}
                    placeholder="290 mi · gas + food stop in Salina" value={notes}
                    onChange={(e) => setNotes(e.target.value)} />
        </div>

        {err && <p className={styles.error} role="alert">{err}</p>}
      </div>
    </Modal>
  );
}

/**
 * The MEMBERS control — a type-ahead crew search over ONE list of member rows. Each row is
 * avatar · name · role, plus (money seat) a `$` pay input, plus a remove ×; the pulled per-diem is stated
 * once as a footer. Combines what used to be a chip picker + a separate pay list into the reference's single
 * field. Adding rides the shared `Picker` type-ahead (the same control `ChipPicker` builds on); the crew list
 * is the band team, minus who is already on the day.
 */
function MembersField({
  memberIds, onChange, canMoney, payFor, onSetPay, perDiemCents,
}: {
  memberIds: string[];
  onChange: (ids: string[]) => void;
  canMoney: boolean;
  payFor: (personId: string) => string;
  onSetPay: (personId: string, amount: string) => void;
  perDiemCents: number | null;
}) {
  const team = useBandTeam();
  const seats = (team.data?.items ?? []).filter((s): s is typeof s & { personId: string } => !!s.personId);
  const byId = new Map(seats.map((s) => [s.personId, s]));
  const jobOf = (personId: string) => {
    const role = byId.get(personId)?.role;
    return role ? bandRoleLabel(role) : "";
  };
  const nameOf = (personId: string) => byId.get(personId)?.displayName ?? "Member";

  // The crew still addable: everyone with a seat, minus who is already on the day and minus the artist/owner
  // account — it IS the band, not a person you can put on a day (core.is_artist_account).
  const options: PickerOption[] = seats
    .filter((s) => !s.isOwner && !memberIds.includes(s.personId))
    .map((s) => ({
      value: s.personId,
      label: s.displayName ?? "Member",
      render: (
        <PersonPickerOption
          personId={s.personId}
          displayName={s.displayName ?? "Member"}
          avatarMediaId={s.avatarMediaId ?? null}
          jobLabel={s.role ? bandRoleLabel(s.role) : ""}
          roleKey={s.role ?? null}
        />
      ),
    }));

  return (
    <div className={styles.membersField}>
      <Picker
        label="Members"
        typeahead
        value=""
        options={options}
        placeholder="Search members or crew"
        onChange={(id) => { if (id && !memberIds.includes(id)) onChange([...memberIds, id]); }}
      />

      {memberIds.length > 0 && (
        <ul className={styles.memberRows}>
          {memberIds.map((id) => {
            const name = nameOf(id);
            return (
              <li key={id} className={styles.memberRow}>
                <PersonAvatar personId={id} avatarMediaId={byId.get(id)?.avatarMediaId ?? null}
                  initials={orgInitials(name)} size="sm" shape="circle" />
                <span className={styles.memberName}>{name}</span>
                <span className={styles.memberRole}>
                  {jobOf(id)}
                  <AvMark roleKey={byId.get(id)?.role} />
                </span>
                {canMoney && (
                  <div className={styles.payInputWrap}>
                    <span className={styles.payDollar} aria-hidden="true">$</span>
                    <input
                      className={styles.input}
                      type="number" inputMode="decimal" min="0" step="0.01" placeholder="0"
                      aria-label={`Pay for ${name}`}
                      value={payFor(id)}
                      onChange={(e) => onSetPay(id, e.target.value)}
                    />
                  </div>
                )}
                <button type="button" className={styles.memberRemove} aria-label={`Remove ${name}`}
                  onClick={() => onChange(memberIds.filter((x) => x !== id))}>×</button>
              </li>
            );
          })}
        </ul>
      )}

      {canMoney && (
        <p className={styles.perDiemFooter}>
          {perDiemCents == null ? "No per diem set yet" : `Per diem is ${usd(perDiemCents)} per member`}
        </p>
      )}
    </div>
  );
}

